import { type FC, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/Checkbox';
import { cn } from '@/utils/cn';
import { Card, CardHeader, CardTitle, CardBody, CardFooter } from '@/components/Card/Card';
import { Button } from '@/components/Button/Button';
import { getAcceptanceStatus, acceptTerms, type AcceptanceStatus } from '@/services/terms';
import { useDialogFocus } from '@/hooks/useDialogFocus';

/**
 * TermsUpdateModal props
 */
export interface TermsUpdateModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback when terms are accepted */
  onAccept: () => void;
}

/**
 * TermsUpdateModal - Shown when user needs to re-accept updated terms
 *
 * This modal blocks app access until the user accepts the updated
 * Terms of Service and/or Privacy Policy. Used for GDPR compliance.
 */
export const TermsUpdateModal: FC<TermsUpdateModalProps> = ({
  isOpen,
  onAccept,
}) => {
  const [status, setStatus] = useState<AcceptanceStatus | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load acceptance status on mount
  useEffect(() => {
    const loadStatus = async () => {
      if (!isOpen) return;

      setIsLoading(true);
      try {
        const acceptanceStatus = await getAcceptanceStatus();
        setStatus(acceptanceStatus);
      } catch (err) {
        console.error('Failed to load terms status:', err);
        setError('Failed to load terms status. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadStatus();
  }, [isOpen]);

  const handleAccept = async () => {
    if (!termsAccepted || !privacyAccepted) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await acceptTerms();
      onAccept();
    } catch (err) {
      console.error('Failed to accept terms:', err);
      setError('Failed to accept terms. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = termsAccepted && privacyAccepted && !isSubmitting;

  const dialogRef = useRef<HTMLDivElement>(null);
  // Must be answered, so no Escape; focus is still held inside.
  useDialogFocus({ isOpen, containerRef: dialogRef });

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Updated Terms and Privacy Policy"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <Card>
          <CardHeader>
            <CardTitle>Updated Terms & Privacy Policy</CardTitle>
          </CardHeader>

          <CardBody>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-text-secondary">Loading...</div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-status-error mb-4">{error}</p>
                <Button variant="secondary" onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <p className="text-text-secondary">
                    We've updated our Terms of Service and/or Privacy Policy.
                    Please review the changes and accept to continue using Grim Dark Companion.
                  </p>
                </div>

                {/* Show summaries of changes if available */}
                {(status?.termsSummary || status?.privacySummary) && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-text-primary">What's Changed</h3>

                    {!status?.termsCurrent && status?.termsSummary && (
                      <div className="p-3 bg-bg-tertiary rounded border border-border-default">
                        <div className="font-medium text-text-primary text-sm">
                          Terms of Service (v{status.currentTermsVersion})
                        </div>
                        <p className="text-sm text-text-secondary mt-1">
                          {status.termsSummary}
                        </p>
                      </div>
                    )}

                    {!status?.privacyCurrent && status?.privacySummary && (
                      <div className="p-3 bg-bg-tertiary rounded border border-border-default">
                        <div className="font-medium text-text-primary text-sm">
                          Privacy Policy (v{status.currentPrivacyVersion})
                        </div>
                        <p className="text-sm text-text-secondary mt-1">
                          {status.privacySummary}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Terms of Service checkbox */}
                  <div
                    className={cn(
                      'rounded border-2 px-3 transition-all',
                      termsAccepted ? 'border-accent-primary bg-accent-primary/10' : 'border-border-default bg-bg-tertiary hover:border-accent-primary/50'
                    )}
                  >
                    <Checkbox
                      checked={termsAccepted}
                      onChange={setTermsAccepted}
                      label={
                        <span className="block py-2">
                      <span className="block font-semibold text-text-primary">
                        I accept the Terms of Service
                      </span>
                      <span className="mt-1 block text-sm text-text-secondary">
                        I have read and agree to the{' '}
                        <Link
                          to="/terms-and-conditions"
                          target="_blank"
                          className="text-accent-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms of Service
                        </Link>
                        {status && !status.termsCurrent && (
                          <span className="ml-1 text-accent-primary">
                            (v{status.currentTermsVersion})
                          </span>
                        )}
                      </span>
                    </span>
                      }
                    />
                  </div>

                  {/* Privacy Policy checkbox */}
                  <div
                    className={cn(
                      'rounded border-2 px-3 transition-all',
                      privacyAccepted ? 'border-accent-primary bg-accent-primary/10' : 'border-border-default bg-bg-tertiary hover:border-accent-primary/50'
                    )}
                  >
                    <Checkbox
                      checked={privacyAccepted}
                      onChange={setPrivacyAccepted}
                      label={
                        <span className="block py-2">
                      <span className="block font-semibold text-text-primary">
                        I accept the Privacy Policy
                      </span>
                      <span className="mt-1 block text-sm text-text-secondary">
                        I have read and agree to the{' '}
                        <Link
                          to="/privacy-policy"
                          target="_blank"
                          className="text-accent-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </Link>
                        {status && !status.privacyCurrent && (
                          <span className="ml-1 text-accent-primary">
                            (v{status.currentPrivacyVersion})
                          </span>
                        )}
                      </span>
                    </span>
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </CardBody>

          <CardFooter>
            <div className="w-full">
              <Button
                variant="primary"
                className="w-full"
                onClick={handleAccept}
                disabled={!canSubmit}
                isLoading={isSubmitting}
              >
                Accept and Continue
              </Button>
              <p className="text-xs text-text-secondary text-center mt-3">
                You must accept these terms to continue using the service.
              </p>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
